import { DIVSlot } from "@/flokit/core/slots";
import { getSlotChild } from "@/flokit/core/slots/slot-children";
import { CSSDimension } from "@/flokit/types/css-dimension";
import { cn } from "@/flokit/utils/styling/cn";
import { ReactNode, useRef } from "react";
import { IsInModalContext } from "./is-in-modal-context";

export const Header = DIVSlot();
export const Content = DIVSlot();
export const Footer = DIVSlot();

export const ModalHeader = Header;
export const ModalContent = Content;
export const ModalFooter = Footer;

interface ContainerProps {
  children: ReactNode;
  width?: CSSDimension;
  height?: CSSDimension;
  /**
   * TODO: DESIGN to revisit other modals and make sure they work with this, then we don't need this prop
   */
  isForward?: boolean;
}

export const Container = ({ width = "620px", ...props }: ContainerProps) => {
  const header = getSlotChild(props.children, Header);
  const content = getSlotChild(props.children, Content);
  const footer = getSlotChild(props.children, Footer);

  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <IsInModalContext.Provider value={true}>
      <div
        className={cn(
          // on mobile full-screen modal, switch to typical column layout
          props.isForward ? "flex flex-col md:grid" : "grid",
          // does nothing if display is flex
          "grid-rows-[auto,1fr,auto]",
          props.isForward
            ? "h-0 min-h-screen w-screen rounded-none md:h-[unset] md:max-h-ninety md:min-h-[unset] md:w-[625px] md:rounded-2xl"
            : "md:max-h-ninety md:max-w-[90vw]",
          "mx-auto rounded-md bg-white shadow-lg",
          // If we don't have a footer, then the content will have an unrounded bottom unless we do this
          footer ? "" : "overflow-hidden",
        )}
        style={{
          // TODO: May need some useMeasure; otherwise this will override any passed-in width
          width: props.isForward ? undefined : width,
          height: props.height,
        }}
      >
        <div className="flex-shrink-0">{header}</div>
        <div
          ref={contentRef}
          className="overflow-y-auto"
          // When scrolling content, update the scrollTop CSS variable
          onScroll={() => {
            if (!contentRef.current) {
              return;
            }

            contentRef.current.style.setProperty(
              MODAL_SHELL_CONTENT_SCROLL_TOP_CSS_VARIABLE,
              contentRef.current.scrollTop + "px",
            );
          }}
        >
          {content}
        </div>
        <div className="flex-shrink-0">{footer}</div>
      </div>
    </IsInModalContext.Provider>
  );
};

export const Modal = Container;

/**
 * CSS variable so that elements can subscribe to the content's `scrollTop` outside of the React render cycle. For example, select input menus need this.
 */
export const MODAL_SHELL_CONTENT_SCROLL_TOP_CSS_VARIABLE =
  "--modal-shell-content-scroll-top";
